/**
 * @group Reporting
 * 
 * @summary
 * <blockquote>Unified reporting system for console and PostHog analytics</blockquote>
 * 
 * This package provides a unified reporting system that supports console logging
 * and PostHog analytics. The system uses a single BaseEvent interface and 
 * automatically routes events to the appropriate service based on configuration.
 * 
 * ## Service Selection Logic
 * - **Console Service (Default)**: Used when no specific service is configured
 * - **PostHog Service**: Used for product analytics and user behavior tracking
 * - **Environment Detection**: Automatically detects browser vs server environments
 * 
 * ## Key Features
 * - Single event interface (`BaseEvent`) for all reporting
 * - Automatic service selection based on configuration
 * - Environment-based reporting rules
 * - Comprehensive validation with Result pattern
 * - Server-side Logger integration
 * - Browser and Node.js compatibility
 * - Sampling support for cost control
 * - Graceful fallback on errors
 * 
 * @example
 * ```typescript
 * import { report, BaseEvent } from '@shared/reporting';
 * 
 * // Simple event (automatic service selection)
 * const event: BaseEvent = {
 *   id: 'evt-001',
 *   distinctId: 'user-123',
 *   name: 'user:action',
 *   props: { action: 'click' }
 * };
 * 
 * // Report event (uses appropriate service based on environment)
 * const result = await report(event);
 * result.match({
 *   ok: (success) => console.log('Event reported:', success),
 *   err: (error) => console.error('Failed to report:', error)
 * });
 * ```
 */


// Classes
export { Reporter } from './classes/Reporter';

// Types
export type { BaseEvent } from './types/BaseEvent';

// Services
export { ConsoleService } from './services/ConsoleService';
export { PostHogService } from './services/PostHogService';

// Functions
export * from './functions';

// Constants
export * from './constants';

// Interfaces
export * from './interfaces';

// Types
export * from './types';